import { atomWithStorage, useAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import type { Updater } from "@mdotm/shadcn/legacy/utils";
import { differenceInSeconds } from "date-fns";
import { type FakeAiLoaderStep, stepNames, type FakeAiLoaderStepName } from "./common";

type SerializableStepData = {
	id: string;
	progress: number;
	step: FakeAiLoaderStepName;
	date: Date;
};

export const animationProgressDefault: Array<SerializableStepData> = [];

export const animationProgress$ = atomWithStorage<Array<SerializableStepData>>(
	"animationProgressAtom",
	animationProgressDefault,
);

export function useAnimationProgressById(id: string): SerializableStepData | undefined {
	const animations = useAtomValue(animationProgress$);
	return animations.find((animation) => animation.id === id);
}

export function useAnimationProgressState(): {
	setAnimationProgress: (newValue: SerializableStepData[] | Updater<SerializableStepData[]>) => void;
	animationProgress: Array<SerializableStepData>;
	reset(): void;
} {
	const [animationProgress, setAnimationProgress] = useAtom(animationProgress$);
	return {
		setAnimationProgress,
		animationProgress,
		reset() {
			setAnimationProgress(animationProgressDefault);
		},
	};
}

export function simulateAnimationProgress(
	date: Date,
	progress: number,
	stepName: FakeAiLoaderStepName,
	expCoeff: number,
): FakeAiLoaderStep {
	const deltaProgressInSeconds = differenceInSeconds(new Date(), new Date(date));
	const exceededProgression = Number(expCoeff * deltaProgressInSeconds);
	const simulatedProgression = exceededProgression + Number(progress.toFixed(3));

	if (simulatedProgression <= 1.0) {
		return { stepName, progress: simulatedProgression };
	}

	const deltaSimulatedProgressToNewStep = Math.abs(Number(Math.floor(simulatedProgression - 1).toFixed(3)));
	const currentStepIndex = stepNames.indexOf(stepName);
	const nextStep =
		currentStepIndex > -1 && currentStepIndex < stepNames.length - 1 ? stepNames[currentStepIndex + 1] : stepName;
	return { stepName: nextStep, progress: deltaSimulatedProgressToNewStep >= 1 ? 1 : deltaSimulatedProgressToNewStep };
}
